import { call, put, takeLatest, race, take } from "redux-saga/effects";
import i18next from "i18next";
import { setRequestInProcess } from "../request/actions";
import * as actions from "./actions";
import invoicesApiService from "../../services/invoicesApiService";
import { invoiceAdaptor, invoiceStatusChangeAdaptor } from "../../utils/invoiceAdaptors";
import {
  FETCHING_INVOICE,
  DEACTIVATE_STATEMENT,
  DOWNLOAD_INVOICE,
  GO_TO_PAYMENT_GATEWAY,
  PRINT_PAYMENT,
  REFRESH_WEBHOOK,
  CHECK_WEBHOOK_STATUS,
  CHECK_VOID_AVAILABLE,
  VOID_INVOICE,
  VOID_INVOICE_STATUS,
  CHANGE_INVOICE_STATUS,
  FETCHING_INVOICE_CANCEL
} from "./types";
import { downloadBase64AsFile } from "../../utils/fileHelpers";
import { addDataToErrors } from "../../utils/helpers";
import { hideModal, showModal } from "../modal/actions";
import modalNames from "../../constants/modalNames";
import { UNPAID, PENDING, PAID } from "../../constants/invoiceStatuses";

function* fetchInvoiceAsync(action) {
  try {
    yield put(setRequestInProcess(FETCHING_INVOICE, true));

    const { id, skipErrorHandling } = action.payload;
    const { response, cancelFetch } = yield race({
      response: call(x => invoicesApiService.getInvoice(x), id),
      cancelFetch: take(FETCHING_INVOICE_CANCEL)
    });

    yield put(setRequestInProcess(FETCHING_INVOICE, false));

    if (cancelFetch) {
      return;
    }

    if (response.hasError) {
      yield put(actions.fetchingInvoiceSuccess(null));
      yield put(actions.fetchingInvoiceError(response.errors));
      if (!skipErrorHandling) {
        yield put(showModal(modalNames.ERROR_MODAL, { errors: response.errors }));
      }
    } else {
      yield put(actions.fetchingInvoiceError(null));
      yield put(actions.changeInvoiceStatusError(null));
      yield put(actions.fetchingInvoiceSuccess(invoiceAdaptor(response.payload)));
    }
  } catch (err) {
    yield put(actions.fetchingInvoiceError([err.message]));
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchFetchInvoiceAsync() {
  yield takeLatest(FETCHING_INVOICE, fetchInvoiceAsync);
}

function* deactivateStatementAsync(action) {
  try {
    yield put(setRequestInProcess(DEACTIVATE_STATEMENT, true));

    const statementId = action.payload;
    const response = yield call(x => invoicesApiService.deactivateStatement(x), statementId);

    yield put(setRequestInProcess(DEACTIVATE_STATEMENT, false));

    if (response.hasError) {
      yield put(actions.deactivateStatementError(response.errors));
      yield put(
        showModal(modalNames.ERROR_MODAL, {
          errors: addDataToErrors(response.errors, { statementId })
        })
      );
    } else {
      yield put(actions.deactivateStatementSuccess(response.payload));
    }
  } catch (err) {
    yield put(actions.deactivateStatementError([err.message]));
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchDeactivateStatementAsync() {
  yield takeLatest(DEACTIVATE_STATEMENT, deactivateStatementAsync);
}

function* downloadInvoiceAsync(action) {
  try {
    const { id, lng } = action.payload;

    yield put(setRequestInProcess(DOWNLOAD_INVOICE, id));

    const response = yield call((x, y) => invoicesApiService.downloadInvoice(x, y), id, lng);

    yield put(setRequestInProcess(DOWNLOAD_INVOICE, false));

    if (response.hasError) {
      yield put(showModal(modalNames.ERROR_MODAL, { errors: response.errors }));
      yield put(actions.downloadInvoiceError(response.errors));
    } else {
      const { fileName, data } = response.payload;
      if (fileName && data) {
        downloadBase64AsFile(data, fileName, "application/pdf");
      }
      yield put(actions.downloadInvoiceSuccess(response.payload));
    }
  } catch (err) {
    yield put(actions.downloadInvoiceError([err.message]));
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchDownloadInvoiceAsync() {
  yield takeLatest(DOWNLOAD_INVOICE, downloadInvoiceAsync);
}

function* goToPaymentGatewayAsync(action) {
  try {
    yield put(setRequestInProcess(GO_TO_PAYMENT_GATEWAY, true));

    const response = yield call(() => invoicesApiService.getPaymentGatewayToken());
    yield put(setRequestInProcess(GO_TO_PAYMENT_GATEWAY, false));

    if (response.hasError) {
      yield put(showModal(modalNames.ERROR_MODAL, { errors: response.errors }));
      yield put(actions.goToPaymentGatewayError(response.errors));
    } else {
      const { subToken, token } = response.payload;
      const invoice = action.payload;
      const { currency, amount, code } = invoice;
      const pgwUrl = process.env.REACT_APP_PAYMENT_GATEWAY_URL;

      yield put(actions.goToPaymentGatewaySuccess(response.payload));

      // eslint-disable-next-line no-undef
      window.location.href = `${pgwUrl}/${currency}/${token}/${subToken}/${code}/${amount}?lng=${i18next.language}`;
    }
  } catch (err) {
    yield put(actions.goToPaymentGatewayError([err.message]));
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchGoToPaymentGatewayAsync() {
  yield takeLatest(GO_TO_PAYMENT_GATEWAY, goToPaymentGatewayAsync);
}

function* printPaymentAsync(action) {
  try {
    const { id, lng } = action.payload;
    yield put(setRequestInProcess(PRINT_PAYMENT, true));

    const response = yield call((x, y) => invoicesApiService.printPayment(x, y), id, lng);

    yield put(setRequestInProcess(PRINT_PAYMENT, false));

    if (response.hasError) {
      yield put(actions.printPaymentError(response.errors));
    } else {
      const { fileName, data } = response.payload;
      if (fileName && data) {
        downloadBase64AsFile(data, fileName, "application/pdf");
      }
      yield put(actions.printPaymentSuccess(response.payload));
    }
  } catch (err) {
    yield put(actions.printPaymentError([err.message]));
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchPrintPaymentAsync() {
  yield takeLatest(PRINT_PAYMENT, printPaymentAsync);
}

function* refreshWebHooksAsync(action) {
  try {
    yield put(setRequestInProcess(REFRESH_WEBHOOK, true));
    const response = yield call(() => invoicesApiService.refreshWebHook(action.payload));

    yield put(setRequestInProcess(REFRESH_WEBHOOK, false));

    yield put(actions.fetchInvoice({ id: action.payload.invoiceId }));

    if (response.hasError) {
      yield put(actions.refreshWebHooksError(response.errors));
    } else {
      const { isAttemptsMaxedOut } = response.payload;

      yield put(actions.checkWebhookStatusSuccess(isAttemptsMaxedOut));
      yield put(actions.refreshWebHooksSuccess(response.payload));
    }
  } catch (err) {
    yield put(actions.refreshWebHooksError([err.message]));
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchRefreshWebHooksAsync() {
  yield takeLatest(REFRESH_WEBHOOK, refreshWebHooksAsync);
}

function* checkWebHooksAsync(action) {
  try {
    yield put(setRequestInProcess(CHECK_WEBHOOK_STATUS, true));
    const response = yield call(() => invoicesApiService.checkWebHook(action.payload));

    yield put(setRequestInProcess(CHECK_WEBHOOK_STATUS, false));

    if (response.hasError) {
      yield put(actions.checkWebhookStatusError(response.errors));
    } else {
      const { isAttemptsMaxedOut } = response.payload;

      yield put(actions.checkWebhookStatusSuccess(isAttemptsMaxedOut));
    }
  } catch (err) {
    yield put(actions.checkWebhookStatusError([err.message]));
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchCheckWebHooksAsync() {
  yield takeLatest(CHECK_WEBHOOK_STATUS, checkWebHooksAsync);
}

function* checkVoidAvailableAsync(action) {
  try {
    const response = yield call(() => invoicesApiService.checkVoidAvailable(action.payload));

    if (response.hasError) {
      console.error(response.errors);
    } else {
      const { voidable } = response.payload;
      yield put(actions.checkVoidAvailableSuccess(voidable));
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchCheckVoidAvailableAsync() {
  yield takeLatest(CHECK_VOID_AVAILABLE, checkVoidAvailableAsync);
}

function* voidInvoiceAsync(action) {
  try {
    yield put(setRequestInProcess(VOID_INVOICE_STATUS, true));
    const response = yield call(() => invoicesApiService.voidInvoice(action.payload));
    if (response.hasError) {
      yield put(
        showModal(modalNames.ERROR_MODAL, {
          errors: response.errors.map(err => ({ ...err, data: { data: action.payload.data } }))
        })
      );
    } else {
      yield put(actions.fetchingInvoiceSuccess(invoiceAdaptor(response.payload)));
      yield put(actions.checkVoidAvailableSuccess(false));
      yield put(hideModal(modalNames.VOID_MODAL));
    }
    yield put(setRequestInProcess(VOID_INVOICE_STATUS, false));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

function* watchVoidInvoiceAsync() {
  yield takeLatest(VOID_INVOICE, voidInvoiceAsync);
}

function* changeInvoiceStatusAsync(action) {
  try {
    yield put(setRequestInProcess(CHANGE_INVOICE_STATUS, true));

    const { status } = action.payload;

    const dataToSend = invoiceStatusChangeAdaptor(action.payload);

    let response;

    if (status === UNPAID) {
      response = yield call(() => invoicesApiService.changeInvoiceStatus({ ...dataToSend, notificationCode: PENDING }));

      if (!response.hasError) {
        response = yield call(() => invoicesApiService.changeInvoiceStatus({ ...dataToSend, notificationCode: PAID }));
      }
    } else if (status === PENDING) {
      response = yield call(() => invoicesApiService.changeInvoiceStatus({ ...dataToSend, notificationCode: PAID }));
    }

    yield put(setRequestInProcess(CHANGE_INVOICE_STATUS, false));
    yield put(hideModal(modalNames.INVOICE_PROCESSING_MODAL));

    if (response && response.hasError) {
      yield put(actions.changeInvoiceStatusError(response.errors));
      yield put(actions.changeInvoiceStatusSuccess(null));
    } else {
      yield put(
        actions.changeInvoiceStatusSuccess({ id: response?.payload?.invoiceId, msgKey: "Invoice Paid Success" })
      );
      yield put(actions.changeInvoiceStatusError(null));
    }
  } catch (err) {
    // Handle errors
    console.error(err);
  }
}

function* watchChangeInvoiceStatusAsync() {
  yield takeLatest(CHANGE_INVOICE_STATUS, changeInvoiceStatusAsync);
}

export {
  watchFetchInvoiceAsync,
  watchDeactivateStatementAsync,
  watchDownloadInvoiceAsync,
  watchGoToPaymentGatewayAsync,
  watchPrintPaymentAsync,
  watchRefreshWebHooksAsync,
  watchCheckWebHooksAsync,
  watchCheckVoidAvailableAsync,
  watchVoidInvoiceAsync,
  watchChangeInvoiceStatusAsync
};
